import React, { type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useWindows } from "@app/overlay";
import { OPEN_FILTER_QUERY_PARAM } from "@app/router/queryParams";
import { CardHeader } from "@shared/ui/card-header";
import { ListItem } from "@src/components/business-list";
import { BusinessList } from "@src/components/business-list";
import { BusinessBoStateValue, BusinessDateValue, BusinessDivisionValue } from "@src/components/business-data-display";
import { useBusinessListQuery } from "@src/hooks/useBusinessListQuery";
import { useUrlFilter } from "@shared/hooks/useUrlFilter";
import { useUrlParamTrigger } from "@shared/hooks/useUrlParamTrigger";
import {
    stockGatheringsServices,
    type StockGathering,
    type StockGatheringExtends,
    type StockGatheringFilter,
} from "../../../../api/stockGatherings";
import { stockGatheringCompletionListWindows } from "./stockGatheringCompletionList.windows";
import "./StockGatheringCompletionList.scss";

/** Include списка КВ для комплектования. */
const STOCK_GATHERING_COMPLETION_LIST_INCLUDES = {
    include: ["SenderDivision", "SenderSectionData", "ReceiverDivision", "ReceiverSectionData", "State"] satisfies Array<
        keyof StockGatheringExtends
    >,
} as const;

/** Список комплектовочных ведомостей для сценария комплектования по КВ. */
export const StockGatheringCompletionList: FC = () => {
    const windows = useWindows();
    const navigate = useNavigate();
    const filterState = useUrlFilter<StockGatheringFilter>();

    const list = useBusinessListQuery<StockGathering, StockGatheringFilter>({
        queryKey: "stock.gatheringCompletion.gatherings",
        filter: filterState.filter,
        queryFn: async (flt, signal) => {
            const result = await stockGatheringsServices.load(
                {
                    flt,
                    ...STOCK_GATHERING_COMPLETION_LIST_INCLUDES,
                },
                { signal },
            );
            return result;
        },
        getId: (item) => item.Code,
    });

    const openFilterWindow = (): void => {
        stockGatheringCompletionListWindows.filter.open(windows, {
            initialFilter: filterState.filter,
            onApply: (flt) => {
                filterState.applyFilter(flt, true);
            },
        });
    };

    useUrlParamTrigger(OPEN_FILTER_QUERY_PARAM, () => {
        openFilterWindow();
    });

    return (
        <div className="stock_gathering_completion_list_page">
            <CardHeader
                text="Комплектация по КВ"
                buttons={[
                    {
                        type: "button",
                        text: "Поиск",
                        srcIcon: "/images/common/options.svg",
                        onClick: openFilterWindow,
                    },
                ]}
            />
            <BusinessList list={list} onOpenFilter={openFilterWindow}>
                {list.data.map((item) => (
                    <ListItem
                        key={item.Code}
                        title={`КВ №${item.Num}`}
                        props={[
                            { label: "Дата", value: <BusinessDateValue value={item.DocDate} /> },
                            { label: "Статус КВ", value: <BusinessBoStateValue state={item.State} /> },
                            {
                                label: "Отправитель",
                                value: (
                                    <BusinessDivisionValue division={item.SenderDivision} section={item.SenderSectionData} />
                                ),
                            },
                            {
                                label: "Получатель",
                                value: (
                                    <BusinessDivisionValue
                                        division={item.ReceiverDivision}
                                        section={item.ReceiverSectionData}
                                    />
                                ),
                            },
                        ]}
                        onClick={() => {
                            navigate({ pathname: `${item.Code}` });
                        }}
                    />
                ))}
            </BusinessList>
        </div>
    );
};
